import React, { useState, useEffect, useRef } from "react";
import L from "leaflet";
import gsap from 'gsap';
import sattIcon from '../plugins/LayersControl/icons/satt.png';
import osmIcon from '../plugins/LayersControl/icons/osm.png';
import { Filter, InfoExtended } from '../components/Filter';
import { useQuery, useLazyQuery } from "@apollo/client";
import { MALFUNCTIONS_QUERY } from "../queries/BrokenElevators";
import { GET_stations_select, GET_stations } from "../queries/Map";
import { ColorRing } from 'react-loader-spinner';


//function contains layer name, data, style

const geoJSONStyles = {
    'normal': {
        radius: 8,
        fillColor: "#545454",
        color: "#000",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
    }, 'anchor': {
        radius: 8,
        fillColor: "#324aa8",
        color: "#000",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
    }, 'spaceconnection': {
        radius: 8,
        fillColor: "#32a85c",
        color: "#000",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
    }, 'poi': {
        radius: 8,
        fillColor: "#ed1111",
        color: "#000",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
    }, 'qrCode': {
        radius: 8,
        fillColor: "#f542ec",
        color: "#000",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
    },
    'routeEdgeNone': {
        color: "#404040",
    },
    'routeEdgeDone': {
        color: "#009900",
    },
    'routeEdgeCorr': {
        color: "#0000ff",
    },
    'routeEdgeIncorr': {
        color: "#cc0000",
    }
};

function calculateMiddlePoint(coord1, coord2) {
    // Extract latitude and longitude from each coordinate
    const lat1 = coord1[0];
    const lon1 = coord1[1];
    const lat2 = coord2[0];
    const lon2 = coord2[1];

    // Convert latitude and longitude from degrees to radians
    const radLat1 = lat1 * Math.PI / 180;
    const radLon1 = lon1 * Math.PI / 180;
    const radLat2 = lat2 * Math.PI / 180;
    const radLon2 = lon2 * Math.PI / 180;

    // Calculate the middle point
    const dLon = radLon2 - radLon1;
    const Bx = Math.cos(radLat2) * Math.cos(dLon);
    const By = Math.cos(radLat2) * Math.sin(dLon);
    const lat3 = Math.atan2(Math.sin(radLat1) + Math.sin(radLat2), Math.sqrt((Math.cos(radLat1) + Bx) * (Math.cos(radLat1) + Bx) + By * By));
    const lon3 = radLon1 + Math.atan2(By, Math.cos(radLat1) + Bx);

    // Convert the result back to degrees
    const middleLat = lat3 * 180 / Math.PI;
    const middleLon = lon3 * 180 / Math.PI;

    // Return the middle point as [latitude, longitude]
    return [middleLat, middleLon];
}

function calculateAngle(coord1, coord2) {
    // Calculate the differences in longitude and latitude
    const dLon = coord2[1] - coord1[1];
    const dLat = coord2[0] - coord1[0];

    // Calculate the angle in radians
    let angle = Math.atan2(dLat, dLon);

    // Convert the angle to degrees
    angle = angle * (180 / Math.PI);

    // Ensure the angle is in the range [0, 360)
    if (angle < 0) {
        angle += 360;
    }

    return Math.round(angle) - 90;
}

function createHTMLTagsFromObject(obj) {
    var htmlTags = '';

    for (var key in obj) {
        if (obj.hasOwnProperty(key)) {
            var value = obj[key];

            if (Array.isArray(value)) {
                // Handle arrays of objects or nested arrays
                var arrayTags = '';
                for (var i = 0; i < value.length; i++) {
                    var arrayItem = value[i];
                    if (typeof arrayItem === 'object') {
                        var nestedTags = createHTMLTagsFromObject(arrayItem);
                        arrayTags += nestedTags;
                    } else {
                        arrayTags += '<div style="padding-left: 20px;">' + arrayItem + '</div>';
                    }
                }
                var tag = '<div style="padding-left: 20px;"><strong>' + key + ':</strong><div style="padding-left: 20px;">' + arrayTags + '</div></div>';
                htmlTags += tag;
            } else if (typeof value === 'object') {
                // Handle nested objects
                var nestedTags = createHTMLTagsFromObject(value);
                var tag = '<div><strong>' + key + ':</strong><div style="padding-left: 20px;">' + nestedTags + '</div></div>';
                htmlTags += tag;
            } else {
                // Handle other values
                var tag = '<p><strong>' + key + ':</strong>' + value + '</p>';
                htmlTags += tag;
            }
        }
    }
    return htmlTags;
}

function removeNullAndCoordinates(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        const filtered = obj.map(removeNullAndCoordinates).filter(value => value !== null);
        return filtered.length === 0 ? null : filtered;
    }

    return Object.keys(obj)
        .reduce((acc, key) => {
            const value = removeNullAndCoordinates(obj[key]);
            if (value !== null && !(key === 'coordinates' && Array.isArray(value)) && !(key === 'neighbours') && !(key === 'aim')) {
                acc[key] = value;
            }
            return acc;
        }, {});
}

function removeAnchorTransport(obj) {
    if (obj.properties.anchor) {
        let resAnch = []
        for (let i in obj.properties.anchor) {
            if (obj.properties.anchor[i].type !== 'transport') {
                resAnch.push(obj.properties.anchor[i])
            }
        }
        const result = {
            ...obj,
            properties: {
                ...obj.properties,
                anchor: resAnch
            }
        }
        return result
    } else {
        return obj
    }
}

function Issue(name, namePL, id, source, qst, style) {
    this.name = name;
    this.namePL = namePL
    this.id = id;
    this.source = source;
    this.layer = L.geoJson(this.source, {
        pointToLayer: function PToL(feature, latlng) {
            return L.marker(latlng, style)
        }
    })
    this.style = style;
    this.setStyle = function () {
        return this.layer.setStyle(this.style)
    };
}

let mymap;

const Map = () => {

    //wrapper used for gsap
    const wrapper = useRef(null);

    const [name, setName] = useState({ 'name': "Kliknij w obiekt, aby zobaczyć nazwę!", 'id': "", 'lvlDiff': "" })
    const [infoExtended, setInfoExtended] = useState(false)
    const [selectedStation, setSelectedStation] = useState(null);

    const [levels, setLevels] = useState([]);
    const [units, setUnits] = useState([]);
    const [layers, setLayers] = useState();
    const [verticalOrder, setVerticalOrder] = useState([])

    // Define objects displayed and type of geom
    const objects = [
        { 'schema': 'Network', 'type': 'Point', 'name': 'TransportationUnits' },
        { 'schema': 'Indoor', 'type': 'Point', 'name': 'RouteNode' },
        { 'schema': 'Indoor', 'type': 'LineString', 'name': 'RouteEdge' },
        { 'schema': 'GetGeometries', 'type': 'Polygon', 'name': 'StationSchema' }
    ]

    // Initialize variables
    let levels_spaces = {};
    var vertical = []

    const [getStations, { data, error, loading }] = useLazyQuery(GET_stations);
    const stationsSelect = useQuery(GET_stations_select, {
        variables: {
            networkInput: {
                neighboursType: ['metro station'],
                typeoftransport: ['metro'],
            },
        }
    });

    const downloadStations = () => {
        if (selectedStation)
            getStations({
                variables: {
                    networkInput: {
                        neighboursType: [
                            "metro station"
                        ],
                        typeoftransport: [
                            "metro"
                        ],
                        transportationUnitId: parseInt(selectedStation)
                    },
                    indoorInput: {
                        transportationUnitId: parseInt(selectedStation)
                    },
                    getGeometriesInput: {
                        transportationUnitId: parseInt(selectedStation)
                    },
                }
            })
    }

    useEffect(() => {
        if (selectedStation)
            downloadStations();
    }, [selectedStation])

    useEffect(() => {
        if (!loading && data) {

            //map init
            mymap = L.map('map').setView([data.Network[0].geometry.coordinates[1], data.Network[0].geometry.coordinates[0]], 18, { zoomControl: true });

            // Iterate over the objects array
            for (let b of objects) {
                const elevationObject = {}
                const photoObjects = {};

                if (data[b.schema] && Array.isArray(data[b.schema])) {
                    console.log(data[b.schema], 'SCHEMA')

                    // Check for data type because photos are attached only to the routeEdges
                    if (b.name === 'RouteEdge') {


                        // Iterate through the dataset to generate photo objects
                        data[b.schema].forEach(nodes => {
                            // Create an object to store photos for each route node
                            let node = nodes.properties

                            // Iterate through the dataset to generate photo objects
                            const nodeId = node.routeNode;

                            // Initialize a new photo object for the current node if it doesn't exist
                            if (!photoObjects[nodeId]) {
                                photoObjects[nodeId] = {};
                            }

                            if (node.neighbours) {
                                // Loop through the neighbors of the current node
                                node.neighbours.forEach(neighbor => {
                                    const neighborId = neighbor.aim.id;

                                    // Initialize a new photo array for the neighbor if it doesn't exist
                                    if (!photoObjects[neighborId]) {
                                        photoObjects[neighborId] = {};
                                    }

                                    // Check if the neighbor has an image array
                                    if (Array.isArray(neighbor.image) && neighbor.image.length > 0) {
                                        // Initialize a new array for photos if it doesn't exist
                                        if (!photoObjects[nodeId][neighborId]) {
                                            photoObjects[nodeId][neighborId] = [];
                                        }

                                        // Collect the photos from the neighbor to the photoObject as an array
                                        neighbor.image.forEach(image => {
                                            photoObjects[nodeId][neighborId].push(image.link);
                                        });

                                        // Reverse direction
                                        if (!photoObjects[neighborId][nodeId]) {
                                            photoObjects[neighborId][nodeId] = [];
                                        }

                                        // Collect the photos from the current node to the photoObject as an array
                                        neighbor.image.forEach(image => {
                                            photoObjects[neighborId][nodeId].push(image.link);
                                        });
                                    }
                                });
                            }
                        })

                    }

                    // Iterate over the collections obtained from grapqhl server
                    // Here the data is converted to have the geojson format
                    data[b.schema].map(item => {
                        if (item !== null) {

                            // Initialize the geojson object
                            let item_geojson = removeAnchorTransport({ ...item })
                            item_geojson.type = 'Feature'

                            // Check if the data queried from backend contains properties key
                            if ('properties' in item_geojson && item_geojson.properties !== null) {

                                // Convertion to LineString
                                if (b.name === 'RouteEdge' || b.name === 'TransportationUnitTransitions') {
                                    if (item_geojson.properties.neighbours) {


                                        const nodeId = item_geojson.properties.routeNode;

                                        let neighbours = item_geojson.properties.neighbours

                                        // Iterate over the neighbours
                                        neighbours.map(neigh => {
                                            let geojson_schema = {
                                                'properties': '',
                                                'geometry': '',
                                                'type': 'Feature'
                                            }

                                            // Collect properties from the neighbour
                                            geojson_schema.properties = { ...neigh }

                                            // Assign photos from the object created in the code above
                                            if (b.name === 'RouteEdge') {
                                                geojson_schema.properties.image = photoObjects[nodeId][neigh.aim.id]
                                            }

                                            delete geojson_schema.properties.geometry

                                            // Collect connection from the node to his neighbour and attach in a form of geojson
                                            if (neigh.geometry?.coordinates) {
                                                geojson_schema.geometry = neigh.geometry
                                            }

                                            // Add the floor of the object to array that will be displayed in filtering if it is not there already
                                            if (geojson_schema.properties.floor !== undefined && !vertical.includes(geojson_schema.properties.floor)) {
                                                vertical.push(geojson_schema.properties.floor)
                                            }
                                            // If a layer exists for a given floor add it to the existing
                                            if (geojson_schema.properties.floor && geojson_schema.properties.floor in elevationObject && !elevationObject[geojson_schema.properties.floor]['features'].includes(geojson_schema)) {
                                                elevationObject[geojson_schema.properties.floor]['features'].push(geojson_schema)

                                                //add markers showing directions of routeEdges ">"
                                                if (b.name === 'RouteEdge') {
                                                    if (neigh.geometry?.coordinates) {
                                                        neigh.geometry.coordinates.forEach((coords, key) => {
                                                            if (key > 0) {
                                                                let geojson_schema2 = {
                                                                    'properties': '',
                                                                    'geometry': '',
                                                                    'type': 'Feature'
                                                                }
                                                                delete geojson_schema2.properties.geometry
                                                                geojson_schema2.geometry = {
                                                                    coordinates: calculateMiddlePoint(neigh.geometry.coordinates[key - 1], neigh.geometry.coordinates[key]),
                                                                    type: 'Point'
                                                                }
                                                                geojson_schema2.properties = { angle: calculateAngle(neigh.geometry.coordinates[key - 1], neigh.geometry.coordinates[key]) }
                                                                elevationObject[geojson_schema.properties.floor]['features'].push(geojson_schema2)
                                                            }
                                                        })
                                                    }
                                                }
                                            }
                                            // else create a new layer
                                            else if (geojson_schema.properties.floor) {
                                                elevationObject[geojson_schema.properties?.floor] = {
                                                    type: 'FeatureCollection',
                                                    features: [geojson_schema]
                                                }

                                                //add markers showing directions of routeEdges ">"
                                                if (b.name === 'RouteEdge') {
                                                    if (neigh.geometry?.coordinates) {
                                                        neigh.geometry.coordinates.forEach((coords, key) => {
                                                            if (key > 0) {
                                                                let geojson_schema2 = {
                                                                    'properties': '',
                                                                    'geometry': '',
                                                                    'type': 'Feature'
                                                                }
                                                                delete geojson_schema2.properties.geometry
                                                                geojson_schema2.geometry = {
                                                                    coordinates: calculateMiddlePoint(neigh.geometry.coordinates[key - 1], neigh.geometry.coordinates[key]),
                                                                    type: 'Point'
                                                                }
                                                                geojson_schema2.properties = { angle: calculateAngle(neigh.geometry.coordinates[key - 1], neigh.geometry.coordinates[key]) }
                                                                elevationObject[geojson_schema.properties.floor]['features'].push(geojson_schema2)
                                                            }
                                                        })
                                                    }
                                                }
                                            }
                                            // Defalut behaviour for objects without level property
                                            else if (b.name in elevationObject) {
                                                elevationObject[b.name]['features'].push(geojson_schema)
                                            } else {
                                                elevationObject[b.name] = {
                                                    type: 'FeatureCollection',
                                                    features: [geojson_schema]
                                                }
                                            }
                                        })
                                    }
                                }
                                // Conversion of objects that are not linestrings
                                else {

                                    // Add the floor of the object to array that will be displayed in filtering if it is not there already
                                    if (item_geojson.properties.floor !== undefined && !vertical.includes(item_geojson.properties.floor.vertorder)) {
                                        vertical.push(item_geojson.properties.floor.vertorder)
                                    }
                                    // If a layer exists for a given floor add it to the existing
                                    if (item_geojson.properties.floor && item_geojson.properties.floor.vertorder in elevationObject) {
                                        elevationObject[item_geojson.properties.floor?.vertorder]['features'].push(item_geojson)
                                        // else create a new layer
                                    } else if (item_geojson.properties.floor) {
                                        elevationObject[item_geojson.properties?.floor?.vertorder] = {
                                            type: 'FeatureCollection',
                                            features: [item_geojson]
                                        }
                                    }
                                    // Default behaviour for objects without level property
                                    else if (b.name in elevationObject) {
                                        elevationObject[b.name]['features'].push(item_geojson)
                                    } else {
                                        elevationObject[b.name] = {
                                            type: 'FeatureCollection',
                                            features: [item_geojson]
                                        }
                                    }
                                }
                            }
                        }
                    }
                    )
                }
                // Logic for displaying station schemas
                else if (b.schema === 'GetGeometries') {
                    elevationObject[b.name] = {
                        type: 'FeatureCollection',
                        features: []
                    }

                    data[b.schema]['interiorSpace'].map(item => {
                        if (item !== null && item.geometry !== null) {
                            if (item.floor_name in elevationObject) {
                                elevationObject[item.floor_name]['features'].push({
                                    "type": "Feature",
                                    "properties": {},
                                    "geometry": item.geometry
                                })
                            } else if (item.floor_name) {
                                elevationObject[item.floor_name] = {
                                    type: 'FeatureCollection',
                                    features: [{
                                        "type": "Feature",
                                        "properties": {},
                                        "geometry": item.geometry
                                    }]
                                }
                            }
                        }
                    })


                }

                let final_obj = {}

                // Convert earlier created geojson to leaflet geojson layer and define the way they are rendered
                for (let level in elevationObject) {
                    if (elevationObject[level].features) {
                        final_obj[level] = L.geoJson(elevationObject[level],
                            {
                                style: function (feature) {
                                    if (b.name === 'RouteEdge') {
                                        if (feature.properties.image && feature.properties.image.length > 0) {
                                            if (feature.properties.image.length > 1) {
                                                return geoJSONStyles.routeEdgeDone
                                            } else {
                                                const index = feature.properties.image[0].lastIndexOf('.');
                                                if (feature.properties.image[0][index - 1] === '1')
                                                    return geoJSONStyles.routeEdgeCorr
                                                else
                                                    return geoJSONStyles.routeEdgeIncorr
                                            }
                                        }
                                        return geoJSONStyles.routeEdgeNone
                                    }
                                },
                                pointToLayer: function PToL(feature, latlng) {
                                    // Define the style of the generated geometries and the type of the marker
                                    if (feature.properties.longName) {
                                        return L.marker(latlng, {
                                            weight: 2,
                                            color: '#224fa3'
                                        }).bindTooltip('TU:' + feature.properties.transportationUnitId.toString(), { permanent: true }).openTooltip()
                                    }

                                    if (b.name === 'RouteNode') {
                                        let markerStyle;

                                        if (feature.properties.spaceconnection) {
                                            markerStyle = geoJSONStyles.spaceconnection;
                                        } else if (feature.properties.anchor) {
                                            markerStyle = geoJSONStyles.anchor;
                                        } else if (feature.properties.poi) {
                                            markerStyle = geoJSONStyles.poi;
                                        } else if (feature.properties.qrcode) {
                                            markerStyle = geoJSONStyles.qrCode;
                                        } else {
                                            markerStyle = geoJSONStyles.normal;
                                        }
                                        return L.circleMarker(latlng, markerStyle)
                                    } else if (b.name === 'StationSchema') {
                                        return L.polygon(latlng)
                                    } else if (b.name === 'RouteEdge') {
                                        const icon = L.divIcon({ className: 'arrow-icon', bgPos: [5, 5], html: '<div style="transform: rotate(' + feature.properties.angle.toString() + 'deg) scale(3)">></div>' });
                                        return L.marker(latlng, { icon: icon })
                                    }
                                },
                                onEachFeature: function (feature, layer) {
                                    if (feature.properties) {
                                        if (feature.properties?.routeEdge) {
                                            layer.bindTooltip('RE:' + feature.properties.routeEdge.toString(), { permanent: true }).openTooltip();
                                        }
                                        const result = removeNullAndCoordinates(feature.properties);
                                        // Code for displaying images
                                        if (feature.properties.image && feature.properties.image.length > 0) {
                                            var container = document.createElement('div');
                                            container.style.display = 'flex';
                                            container.style.flexDirection = 'column';
                                            container.style.justifyContent = 'center';
                                            // Create an array of image links
                                            var imageLinks = result.image;

                                            // Loop through the image links and add image elements to the container
                                            for (var i = 0; i < imageLinks.length; i++) {
                                                var image = document.createElement('img');
                                                image.src = imageLinks[i];
                                                image.style.maxWidth = '50%'; // Set maximum width
                                                image.style.alignSelf = 'center';
                                                image.style.maxHeight = '200px'; // Set maximum height
                                                image.alt = 'Image is loading'
                                                container.appendChild(image);
                                            }

                                            // Add the text content to the container
                                            var text = document.createElement('p');
                                            text.style.wordWrap = 'break-word';
                                            text.innerHTML = createHTMLTagsFromObject(result);
                                            container.appendChild(text);

                                            // Bind the popup to the layer
                                            layer.bindPopup(container);

                                        } else {
                                            const htmlTags = createHTMLTagsFromObject(result)
                                            layer.bindPopup(htmlTags)

                                        }
                                    }
                                }
                            })
                    }
                }
                levels_spaces[b.name] = final_obj

            }

            // Variable used to display all of the levels available on the filtering menu
            setVerticalOrder(vertical)

            const info = wrapper.current.children.info

            gsap.set([info], { autoAlpha: 0 });

            const tl = gsap.timeline({ defaults: { ease: 'power3.inOut' } })

            tl.fromTo(info, { y: '-=50' }, { duration: 2, y: '+=50', autoAlpha: 1 })

            //maps max&min zoom levels
            mymap.options.minZoom = 5;
            mymap.options.maxZoom = 30;

            //basemaps which can be choosen
            var providers = {};
            providers['Default'] = {
                title: 'osm',
                icon: osmIcon,
                layer: L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    maxZoom: 30,
                })
            };
            providers['Sattelite'] = {
                title: 'satka',
                icon: sattIcon,
                layer: L.tileLayer('https://api.mapbox.com/styles/v1/bwiktorz/ckdvsgxi02g4d19lli0o110t2/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiYndpa3RvcnoiLCJhIjoiY2p0OGh6OWtuMDhmNDN5cXk4ZTEzMDltYiJ9.UVVfRCnZuTePUEoenfBOKA', {
                    maxZoom: 30,
                })
            };

            //set basemap control - insert basemaps and add control
            var layers = [];
            for (let providerId in providers) {
                layers.push(providers[providerId].layer);
            }

            //zoom control
            mymap.zoomControl.setPosition('bottomright');

            mymap.eachLayer(function (layer) {
                mymap.removeLayer(layer);
            });

            mymap.addLayer(layers[0])

            setLayers(levels_spaces)

        }
    }, [loading])


    // Add or delete leaflet layers based on the objects selected in the filter panel
    useEffect(() => {
        const types = ['TransportationUnits', 'RouteNode', 'RouteEdge', 'StationSchema']

        if (mymap) {

            types.map(unit => {

                if (units.includes(unit)) {
                    // Add and remove layers when the given object has also levels
                    if (levels.length) {
                        verticalOrder.map(lvl => {
                            //add all of the selected levels
                            if (levels.includes(lvl)) {
                                try {
                                    mymap.addLayer(layers[unit][lvl])
                                } catch (e) {

                                }
                            } else {  //filter level layer
                                try {
                                    mymap.removeLayer(layers[unit][lvl])

                                } catch (e) {
                                    console.log(e, 'levelfilters')
                                }
                            }
                        })

                    }
                    // Add and remove object that doesn't have the verticalorder property
                    else {
                        Object.keys(layers[unit]).map(key => {
                            mymap.addLayer(
                                layers[unit][key]
                            )
                        }
                        )//add every level

                    }
                } else {
                    try {
                        Object.keys(layers[unit]).map(key =>
                            mymap.removeLayer(
                                layers[unit][key])
                        )
                    } catch (e) {
                        console.log(e, 'error')
                    }
                    //remove every level
                }
            })
        }

    }, [levels, units])

    console.log(stationsSelect)

    if (stationsSelect.loading)
        return <></>
    else if (selectedStation === null)
        return <div style={{
            margin: 'auto',
            width: '50%',
            border: '3px solid green',
            padding: '10px',
            textAlign: 'center',
            marginTop: '20px'
        }}>
            {
                stationsSelect.data?.Network ?
                    <>
                        <p style={{ fontSize: 30 }}>Wybierz stację</p>
                        <select
                            style={{ fontSize: 30, width: 150 }}
                            value={selectedStation}
                            onChange={e => {
                                setSelectedStation(e.target.value)
                            }}
                        >
                            <option value={null}>.......</option>
                            {
                                [...stationsSelect.data.Network]
                                    .sort((a, b) => a.properties.longName.localeCompare(b.properties.longName, 'pl'))
                                    .map(val => {
                                        return <option value={val.properties.transportationUnitId}>{val.properties.longName}</option>;
                                    })
                            }
                        </select>
                    </>
                    :
                    <p style={{ fontSize: 30 }}>Błąd autoryzacji lub serwera</p>
            }
        </div>
    else
        return (
            <div ref={wrapper} id="map">
                {selectedStation === null ? <></>
                    :
                    loading ?
                        // <div>LOADING</div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100vh' /* This makes the container take up the full viewport height */
                        }}>
                            <ColorRing
                                visible={true}
                                height="80"
                                width="80"
                                ariaLabel="blocks-loading"
                                wrapperStyle={{}}
                                wrapperClass="blocks-wrapper"
                                colors={['#A9A9A9']}
                            />
                        </div>
                        : <>
                            <Filter objects={layers && Object.keys(layers)}
                                downloadStations={downloadStations}
                                verticalorder={verticalOrder.sort((a, b) => -(a - b))} infoWord={name.name}
                                units={units} setUnits={(e) => {
                                    setUnits(e)
                                }} levels={levels} setLevels={(e) => setLevels(e)} />
                            {infoExtended ? <InfoExtended object={name} /> : null}
                        </>
                }
            </div>
        );
}

export default Map;


